import { FC } from 'react';
import { Dialog } from '@mantine/core';
import { MdRefresh } from 'react-icons/md';
import Button from '../../atoms/Button';
import Modal from '../../atoms/Modal';
import { usePortalTarget } from '../../provider';
import AnnounceMegaphone from './AnnounceMegaphone';
import './VersionUpdateDialog.scss';

export interface IVersionUpdateDialogProps {
    /** Предложение на экране */
    opened: boolean;
    /** Сервер не принимает эту версию клиента: окно без «Позже» и без закрытия */
    mandatory?: boolean;
    /** «Позже»: отложить предложение (обязательное не откладывается) */
    onLater: () => void;
    /** «Обновить» @default перезагрузка страницы */
    onReload?: () => void;
}

const reloadPage = (): void => {
    window.location.reload();
};

/**
 * Предложение обновить фронтенд — в двух режимах.
 *
 * Обычный — неблокирующее окно в углу: не затемняет страницу и не перехватывает фокус, пользователь обновляется,
 * когда удобно; «Позже» откладывает предложение. Обязательный — сервер не принимает эту версию клиента: окно без
 * «Позже» и без закрытия.
 */
const VersionUpdateDialog: FC<IVersionUpdateDialogProps> = ({
    opened,
    mandatory = false,
    onLater,
    onReload = reloadPage,
}) => {
    const portalTarget = usePortalTarget();
    const reloadButton = (
        <Button variant="primary" size="sm" onClick={onReload} startAdornment={<MdRefresh size={18} />}>
            Обновить
        </Button>
    );

    return (
        <>
            <Dialog
                opened={opened && !mandatory}
                onClose={onLater}
                withCloseButton={false}
                position={{ bottom: 24, right: 24 }}
                size={380}
                radius={16}
                p={0}
                zIndex={100000}
                transitionProps={{ transition: 'slide-up', duration: 320 }}
                portalProps={portalTarget ? { target: portalTarget } : undefined}
                className="rf-version-update"
            >
                <div className="rf-version-update__body" role="status" aria-live="polite">
                    <span className="rf-version-update__icon" aria-hidden="true">
                        <AnnounceMegaphone />
                    </span>

                    <div className="rf-version-update__content">
                        <p className="rf-version-update__title">Доступно обновление</p>
                        <p className="rf-version-update__text">
                            Мы обновили сервис. Обновите страницу, чтобы продолжить работу с актуальной версией.
                        </p>

                        <div className="rf-version-update__actions">
                            <Button variant="ghost" size="sm" onClick={onLater}>
                                Позже
                            </Button>
                            {reloadButton}
                        </div>
                    </div>
                </div>
            </Dialog>

            <Modal
                opened={opened && mandatory}
                header="Нужно обновить страницу"
                footer={reloadButton}
                size="s"
                closeDisabled
                withCloseButton={false}
                zIndex={100001}
            >
                <div className="rf-version-update__body rf-version-update__body--modal" role="alert">
                    <span className="rf-version-update__icon" aria-hidden="true">
                        <AnnounceMegaphone />
                    </span>
                    <p className="rf-version-update__text">
                        Вышла новая версия сервиса, с этой продолжить работу нельзя. Обновите страницу — правки
                        черновика сохранятся.
                    </p>
                </div>
            </Modal>
        </>
    );
};

export default VersionUpdateDialog;
